import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'

import { ApiError, api } from '@/api/client'

export interface AuthStatus {
  authenticated: boolean
  enrollment_open: boolean
}

interface ChallengeResponse {
  challenge_id: string
  options: unknown
}

export const authStatusKey = ['auth', 'status'] as const

export async function fetchAuthStatus(): Promise<AuthStatus> {
  const { data } = await api.GET('/api/auth/status')
  return data as AuthStatus
}

/**
 * The backend speaks the WebAuthn wire format, which nests the real options
 * under `publicKey`; SimpleWebAuthn wants that inner object.
 */
function unwrapOptions<T>(options: unknown): T {
  if (options && typeof options === 'object' && 'publicKey' in options) {
    return (options as { publicKey: T }).publicKey
  }
  return options as T
}

/** Register a new passkey and sign in with it. */
export async function registerPasskey(label: string): Promise<void> {
  const { data } = await api.POST('/api/auth/register/start', { body: { label } })
  const challenge = data as ChallengeResponse
  const credential = await startRegistration({
    optionsJSON: unwrapOptions<PublicKeyCredentialCreationOptionsJSON>(challenge.options),
  })
  // The credential goes over the wire exactly as SimpleWebAuthn built it:
  // `webauthn-rs` reads the extension outputs from `clientExtensionResults`
  // via a serde alias of its `extensions` field, so adding an `extensions`
  // copy makes serde reject the body as a duplicate field (422).
  await api.POST('/api/auth/register/finish', {
    body: {
      challenge_id: challenge.challenge_id,
      credential,
    },
  })
}

/** Sign in with an already-registered passkey. */
export async function loginWithPasskey(options?: { useAutofill?: boolean }): Promise<void> {
  const { data } = await api.POST('/api/auth/login/start')
  const challenge = data as ChallengeResponse
  const credential = await startAuthentication({
    optionsJSON: unwrapOptions<PublicKeyCredentialRequestOptionsJSON>(challenge.options),
    useBrowserAutofill: options?.useAutofill ?? false,
  })
  await api.POST('/api/auth/login/finish', {
    body: {
      challenge_id: challenge.challenge_id,
      credential,
    },
  })
}

export async function logout(): Promise<void> {
  await api.POST('/api/auth/logout')
}

export interface PasskeySummary {
  id: string
  label: string
  created_at: string
  last_used_at: string | null
  current: boolean
}

export const passkeysKey = ['auth', 'credentials'] as const

export async function fetchPasskeys(): Promise<PasskeySummary[]> {
  const { data } = await api.GET('/api/auth/credentials')
  return (data ?? []) as PasskeySummary[]
}

export async function deletePasskey(id: string): Promise<void> {
  await api.DELETE('/api/auth/credentials/{id}', { params: { path: { id } } })
}

export { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill }

/**
 * Turn whatever a ceremony threw into something worth showing a person.
 * A cancelled prompt is not an error the user needs explaining.
 */
export function describeAuthError(error: unknown): string | null {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError' || error.name === 'AbortError') return null
    if (error.name === 'InvalidStateError') {
      return 'This device already has a passkey for Fundus. Sign in instead.'
    }
    return error.message
  }
  return 'Something went wrong.'
}
